import { useCallback, useEffect, useRef, useState } from 'react';
import { cn } from '@/lib/utils';
import { KeyboardChord } from './KeyboardChord';
import { CopyShortcutsLink } from '@/components/CopyShortcutsLink';

/**
 * State machine for the ⌘⇧E / ⌘⇧S onboarding chord step.
 *
 *   idle ──modifiers held──▶ modifiers_held ──saved=true──▶ save_confirmed ──800ms──▶ done
 *    │                          │                              ▲
 *    │                          └──struggle timeout──▶ idle    │
 *    └──saved=true (cold)─────────────────────────────────────┘
 *
 * Why this design (not a fake 3s in-flight bar):
 *
 *   Chrome's extension keyboard-shortcut binding eats the third keystroke
 *   (E or S) before the page-level keydown handler sees it. We can ONLY
 *   reliably observe ⌘ and ⇧. So we don't pretend to detect the third key:
 *
 *     • idle: chips neutral
 *     • modifiers_held: ⌘/⇧ blue (real keystrokes), third chip PULSES
 *       (we're waiting for it, can't see it directly) — copy reads
 *       "Press <K> now"
 *     • save_confirmed: server reported the save landed. ALL three chips
 *       flash emerald simultaneously + a short 800ms success bar runs as
 *       a real "we got it" beat, NOT a fake in-flight wait.
 *     • done: terminal — parent advances.
 *
 *   The save-confirmed-without-modifiers path is preserved for the case
 *   where the user pressed the shortcut from a different tab (page never
 *   saw modifiers either) or landed on a stale-state replay.
 *
 * Caller responsibilities:
 *   - pass `pressed` (parallel to `keys`) reflecting real keystroke state
 *     for the first two chips; the third entry is IGNORED here (we drive
 *     it from phase, not from caller-reported keystrokes)
 *   - pass `saved` from server polling
 *   - react to `onPhaseChange` to (a) burst-poll on modifiers_held and
 *     (b) advance on `done`
 *   - optional `onSkip` shows a skip button after `skipAfterMs` of
 *     observed struggle (modifiers held with no save). If `onSkip` is
 *     not provided, no skip option appears.
 */
export type ChordPhase = 'idle' | 'modifiers_held' | 'save_confirmed' | 'done';

const STRUGGLE_TIMEOUT_MS = 8000;     // modifiers held this long with no save → show fail copy
const MODIFIERS_RESET_MS = 12000;     // modifiers_held → idle if user gave up entirely
const SUCCESS_BAR_MS = 800;           // celebration bar fill duration after save confirmed
const MIN_MODIFIER_HOLD_MS = 80;      // debounce modifier transients (Mac sometimes fires ghost releases)

interface ChordProgressProps {
  /** Display labels for the chord. Third entry is the "target" key — the
   *  one the extension binds. */
  keys: string[];
  /** Parallel array — real keystroke detection for the first two entries.
   *  The third entry is ignored (we cannot reliably observe it). */
  pressed: boolean[];
  /** Server-side: has the save landed? */
  saved: boolean;
  /** Fires once when transitioning idle → modifiers_held. Use to start
   *  burst polling so we see the save quickly when it lands. */
  onFire?: () => void;
  /** Fires whenever phase changes. Use to drive parent state (e.g., poll
   *  rate, page-level overlays, auto-advance). */
  onPhaseChange?: (phase: ChordPhase) => void;
  /** Hint shown below the chips when phase=idle. */
  idleHint?: string;
  /** Target key label shown in the "Press X now" hint when modifiers held. */
  targetKeyName?: string;
  /** Optional skip handler — when present, a "Skip this step" link appears
   *  after `skipAfterMs` of observed struggle. */
  onSkip?: () => void;
  skipAfterMs?: number;
}

export function ChordProgress({
  keys,
  pressed,
  saved,
  onFire,
  onPhaseChange,
  idleHint,
  targetKeyName,
  onSkip,
  skipAfterMs = STRUGGLE_TIMEOUT_MS,
}: ChordProgressProps) {
  const [phase, setPhase] = useState<ChordPhase>('idle');
  const [successProgress, setSuccessProgress] = useState(0);
  const [showFailCopy, setShowFailCopy] = useState(false);
  const [showSkip, setShowSkip] = useState(false);

  // Stable refs so timer callbacks read current values without re-running.
  const savedRef = useRef(saved);
  useEffect(() => { savedRef.current = saved; }, [saved]);
  const phaseRef = useRef(phase);
  useEffect(() => { phaseRef.current = phase; }, [phase]);
  const onFireRef = useRef(onFire);
  useEffect(() => { onFireRef.current = onFire; }, [onFire]);
  const onPhaseChangeRef = useRef(onPhaseChange);
  useEffect(() => { onPhaseChangeRef.current = onPhaseChange; }, [onPhaseChange]);
  // Ref-mirror for `modifiersDown` so the give-up timer reads the live value
  // (closure capture would freeze it at `true` at phase-entry and the reset
  // would never fire, leaving the 400ms burst polling hot indefinitely).
  const modifiersDownRef = useRef(false);

  // Surface phase changes to parent (effect, not in setPhase callback, so
  // React batching is honored).
  useEffect(() => {
    onPhaseChangeRef.current?.(phase);
  }, [phase]);

  // ── Trigger 1: modifiers held → enter modifiers_held ─────────────────
  // We require both modifier slots (pressed[0] and pressed[1]) to be true.
  // A small hold debounce filters out transient keyup ghosts on Mac.
  const modifiersDown = (pressed[0] ?? false) && (pressed[1] ?? false);
  const modifiersDownSince = useRef<number | null>(null);
  useEffect(() => {
    modifiersDownRef.current = modifiersDown;
    if (modifiersDown && modifiersDownSince.current == null) {
      modifiersDownSince.current = Date.now();
    } else if (!modifiersDown && modifiersDownSince.current != null) {
      // Released — only reset the debounce anchor; don't reset phase here.
      // Phase resets are driven by the explicit timers below.
      modifiersDownSince.current = null;
    }
  }, [modifiersDown]);

  useEffect(() => {
    if (!modifiersDown) return;
    if (phase !== 'idle') return;
    const heldFor = Date.now() - (modifiersDownSince.current ?? Date.now());
    if (heldFor < MIN_MODIFIER_HOLD_MS) {
      // Re-check after the debounce window.
      const t = window.setTimeout(() => {
        if (modifiersDown && phaseRef.current === 'idle') {
          setPhase('modifiers_held');
          setShowFailCopy(false);
          setShowSkip(false);
          onFireRef.current?.();
        }
      }, MIN_MODIFIER_HOLD_MS - heldFor);
      return () => window.clearTimeout(t);
    }
    setPhase('modifiers_held');
    setShowFailCopy(false);
    setShowSkip(false);
    onFireRef.current?.();
  }, [modifiersDown, phase]);

  // ── Trigger 2: saved arrives → save_confirmed ────────────────────────
  // Fires regardless of phase (idle direct path covers cross-tab shortcut
  // or stale-state replay).
  useEffect(() => {
    if (!saved) return;
    if (phaseRef.current === 'save_confirmed' || phaseRef.current === 'done') return;
    setPhase('save_confirmed');
    setShowFailCopy(false);
    setShowSkip(false);
    setSuccessProgress(0);
  }, [saved]);

  // ── modifiers_held → struggle copy + skip surfacing ──────────────────
  useEffect(() => {
    if (phase !== 'modifiers_held') {
      setShowFailCopy(false);
      setShowSkip(false);
      return;
    }
    const struggleTimer = window.setTimeout(() => {
      // Only show fail copy if we still haven't gotten a save.
      if (phaseRef.current === 'modifiers_held' && !savedRef.current) {
        setShowFailCopy(true);
      }
    }, skipAfterMs);
    const skipTimer = window.setTimeout(() => {
      if (phaseRef.current === 'modifiers_held' && !savedRef.current && onSkip) {
        setShowSkip(true);
      }
    }, skipAfterMs);
    const giveUpTimer = window.setTimeout(() => {
      // If the user truly walked away (no modifiers held, no save), drop
      // back to idle so the chord can be re-attempted cleanly later.
      // modifiersDownRef.current is the LIVE value — closing over
      // `modifiersDown` here would freeze it at the phase-entry value
      // (always true) and the reset would never fire.
      if (
        phaseRef.current === 'modifiers_held' &&
        !savedRef.current &&
        !modifiersDownRef.current
      ) {
        setPhase('idle');
      }
    }, MODIFIERS_RESET_MS);
    return () => {
      window.clearTimeout(struggleTimer);
      window.clearTimeout(skipTimer);
      window.clearTimeout(giveUpTimer);
    };
    // We deliberately don't depend on modifiersDown — re-running the timers
    // every time the user releases/re-presses would never let them fire.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase, skipAfterMs, onSkip]);

  // ── save_confirmed → done after 800ms success bar ────────────────────
  useEffect(() => {
    if (phase !== 'save_confirmed') {
      setSuccessProgress(0);
      return;
    }
    let cancelled = false;
    const startTs = performance.now();
    let raf = 0;
    const tick = () => {
      if (cancelled) return;
      const t = Math.min((performance.now() - startTs) / SUCCESS_BAR_MS, 1);
      setSuccessProgress(t);
      if (t < 1) {
        raf = requestAnimationFrame(tick);
      } else {
        setPhase('done');
      }
    };
    raf = requestAnimationFrame(tick);
    return () => {
      cancelled = true;
      if (raf) cancelAnimationFrame(raf);
    };
  }, [phase]);

  const handleSkip = useCallback(() => {
    if (!onSkip) return;
    onSkip();
  }, [onSkip]);

  // ── Render ───────────────────────────────────────────────────────────
  const isLocked = phase === 'save_confirmed' || phase === 'done';
  // When locked, all chips render emerald. Otherwise: first two from real
  // keystrokes; third pulses during modifiers_held to nudge the user.
  const chipsPressed = isLocked
    ? keys.map(() => true)
    : [pressed[0] ?? false, pressed[1] ?? false, false];
  const chipsPulsing = phase === 'modifiers_held' && !isLocked
    ? [false, false, true]
    : keys.map(() => false);

  const targetName = targetKeyName || keys[keys.length - 1] || 'the key';

  return (
    <div className="w-full">
      <div className={cn(isLocked && 'pointer-events-none')}>
        <KeyboardChord
          keys={keys}
          pressed={chipsPressed}
          pulsing={chipsPulsing}
          tone={isLocked ? 'locked' : 'live'}
          size="lg"
        />
      </div>

      <div className="mt-5 min-h-[1.25rem]">
        {phase === 'save_confirmed' || phase === 'done' ? (
          <div className="h-1 w-full overflow-hidden rounded-full bg-emerald-100">
            <div
              className="h-full bg-emerald-500 transition-[width] duration-100 ease-linear"
              style={{ width: `${Math.round(successProgress * 100)}%` }}
            />
          </div>
        ) : phase === 'modifiers_held' && showFailCopy ? (
          <div className="space-y-2">
            <p className="text-xs leading-relaxed text-amber-700">
              The save didn't come through. Your{' '}
              <span className="font-mono">{keys.join(' ')}</span> shortcut may be
              unbound or another extension is using it. Copy the link below and
              paste it into a new tab to check.
            </p>
            <CopyShortcutsLink />
            {showSkip && onSkip && (
              <button
                type="button"
                onClick={handleSkip}
                className="text-xs font-medium text-neutral-600 underline decoration-neutral-300 underline-offset-4 hover:text-neutral-900"
              >
                Skip — I'll set this up later
              </button>
            )}
          </div>
        ) : phase === 'modifiers_held' ? (
          <p className="text-xs text-blue-700">
            Now press <span className="font-mono font-medium">{targetName}</span> to save this page.
          </p>
        ) : idleHint ? (
          <p className="text-xs text-neutral-400">{idleHint}</p>
        ) : null}
      </div>
    </div>
  );
}

